import { formatDateString } from "@/v2-common/utils/utils.date"
import type { LicenseHistoryQuery } from "@/v2-backoffice-client/license/history/__types__/license.history.query"

export type LicenseHistoryEntry =
  LicenseHistoryQuery["getLicenseHistory"][number]

export type LicenseHistoryChange = {
  label: string
  from: string
  to: string
}

// The fields a version can differ on, with how to render each side. Status is
// handled separately by the dialog (it renders as badges, not text).
const DIFF_FIELDS: {
  key: keyof LicenseHistoryEntry
  label: string
  format: (value: unknown) => string
}[] = [
  { key: "Seats", label: "Seats", format: formatDiffNumber },
  { key: "PlanName", label: "License", format: formatDiffText },
  { key: "StartedAt", label: "Started", format: formatDiffDate },
  { key: "RenewsAt", label: "Renews", format: formatDiffDate },
  { key: "TrialEndsAt", label: "Trial ends", format: formatDiffDate },
  { key: "CancelledAt", label: "Cancelled", format: formatDiffDate },
]

function formatDiffNumber(value: unknown) {
  return value === null || value === undefined ? "—" : String(value)
}

function formatDiffText(value: unknown) {
  return typeof value === "string" && value ? value : "—"
}

function formatDiffDate(value: unknown) {
  return typeof value === "string" && value ? formatDateString(value) : "—"
}

/** The field-level changes between a version and the one before it. */
export function getLicenseHistoryChanges(
  entry: LicenseHistoryEntry,
  previous?: LicenseHistoryEntry,
): LicenseHistoryChange[] {
  if (!previous) return []
  return DIFF_FIELDS.filter(
    (field) => (entry[field.key] ?? null) !== (previous[field.key] ?? null),
  ).map((field) => ({
    label: field.label,
    from: field.format(previous[field.key]),
    to: field.format(entry[field.key]),
  }))
}
